// The public site's copy, resolved ONCE per reader from the three inputs it actually has —
// the dictionary for the current language, the tenant's `content_blocks` overrides, and the
// tenant RECORD. `useSiteCopy()` is the only way anything on a public site reads that copy.
//
// Why a hook and not the LanguageProvider
// ---------------------------------------
// `resolveSiteCopy` has always taken a `tenant` (lib/siteContent.js): `contact.email` falls
// back to the practice's real inbox (`business_email` -> `owner_email`) before it falls back
// to the bundled sample "contact@example.com". The provider that used to expose `siteCopy`
// could not pass it: `LanguageProvider` sits ABOVE `TenantProvider` in App.js, and
// `tenant/TenantSite.jsx` nests a SECOND `TenantProvider` per site (the slug comes from the
// route), so no single provider in the tree owns both inputs. Hoisting `TenantProvider` would
// pin the language layer to the outer, platform-level tenant and miss the site's own.
//
// The cost of not resolving it there was paid three times over: Classic's contact section
// printed the sample address while the SAME page's footer swapped in the real one
// (lib/siteLinks.js kept a `=== "contact@example.com"` sentinel to compensate), and
// templates/shared/useSiteData.js re-derived the precedence a third way for t2..t6. One
// resolution, one precedence: override > business_email > owner_email > sample.
//
// Consequence: a component that reads public copy depends on the TenantContext too. Both
// contexts have complete defaults (tenant `null`, EN dictionary, no overrides), so a reader
// mounted outside a tenant — the marketing site, a storybook-style mount — resolves to the
// bundled sample copy instead of throwing.
import { useMemo } from "react";
// Alias imports for the two CONTEXTS (that is what a test fakes) and a relative import for
// the resolver (the real one stays under test — same rationale as LanguageContext.jsx).
import { useLanguage } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
import { resolveSiteCopy, isHiddenRow } from "./siteContent";

/**
 * @returns {object} the dictionary with every editable slot resolved — see
 *   `resolveSiteCopy` for the exact shape (`hero.titleLines`, composed repeaters, `images`).
 */
export function useSiteCopy() {
  const { t, override, overrides } = useLanguage();
  const { tenant } = useTenant();
  // Memoised on the inputs, so a reader keeps one stable object across re-renders (an
  // effect that lists a copy slice in its deps must not re-fire on every parent render).
  return useMemo(() => {
    // A row EXISTS for the key (GET /api/content returns every saved row, empty or not) and
    // is empty in every language: what lets an optional slot be hidden on purpose, while a
    // row that only lacks THIS language's text still shows this language's sample.
    const isCleared = (key) => isHiddenRow(key, (overrides || {})[key]);
    return resolveSiteCopy(t, override, tenant, isCleared);
  }, [t, override, overrides, tenant]);
}
